import { Css, FormSectionLayout, useComputed, useModal } from "@homebound/beam";
import { ObjectConfig, useFormState, useFormStates } from "@homebound/form-state";
import { useMemo } from "react";
import {
  aiProposalsConfig,
  hasPendingProposals,
  toResolveInputs,
} from "src/components/formFields/ai/utils/aiProposalForm";
import { reconcilePendingAiProposals } from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { AiFieldProposalStatus, FeatureFlagType, SaveReadyPlanLocationInput } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageLocationsV2Query,
  PlanPackageLocationsV2_LocationFragment,
  PlanPackageLocationsV2_PlanPackageFragment,
  usePlanPackageLocationsV2Query,
  useResolvePlanPackageLocationAiProposalsMutation,
  useSavePlanPackageLocationsV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/LocationsTab.generated";
import { PlanPackageAiReviewBanner } from "src/routes/libraries/plan-package-v2/components/PlanPackageAiReviewBanner";
import { AddLocationModal } from "src/routes/libraries/plan-package-v2/components/locations/AddLocationModal";
import { LevelLocationsTable } from "src/routes/libraries/plan-package-v2/components/locations/LevelLocationsTable";
import {
  FieldProposalLookup,
  RoomProposalsLookup,
  RoomSummary,
  deriveRoomWarnings,
  flattenRoomProposals,
  indexRoomProposals,
  roomNeedsDelete,
  roomProposalBindings,
} from "src/routes/libraries/plan-package-v2/components/locations/locationProposalUtils";
import {
  RoomFormValue,
  formatFullRoomNumber,
  groupRoomLocations,
  isNumberedLevel,
} from "src/routes/libraries/plan-package-v2/components/locations/locationUtils";
import { queryResult } from "src/utils";

type LocationsTabProps = { planPackageId: string; versionId: string };

export function LocationsTab(props: LocationsTabProps) {
  const { planPackageId, versionId } = props;
  const query = usePlanPackageLocationsV2Query({ variables: { id: planPackageId, versionId } });
  return queryResult(query, ({ planPackage, levels, roomTypes }) => (
    <DataView planPackage={planPackage} levels={levels} roomTypes={roomTypes} refetch={() => query.refetch()} />
  ));
}

type DataViewProps = {
  planPackage: PlanPackageLocationsV2_PlanPackageFragment;
  levels: PlanPackageLocationsV2Query["levels"];
  roomTypes: PlanPackageLocationsV2Query["roomTypes"];
  refetch: ReturnType<typeof usePlanPackageLocationsV2Query>["refetch"];
};

function DataView(props: DataViewProps) {
  const { planPackage, levels, roomTypes, refetch } = props;
  const [save] = useSavePlanPackageLocationsV2Mutation();
  const [resolveProposals] = useResolvePlanPackageLocationAiProposalsMutation();
  const navigateToRpav = useNavigateToRpav();
  const { openModal } = useModal();
  const aiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesAi);

  const groups = useMemo(() => groupRoomLocations(planPackage.roomLocations), [planPackage]);
  const roomProposals = useMemo(() => indexRoomProposals(planPackage, aiEnabled), [planPackage, aiEnabled]);

  /** Single save path for every location write. */
  async function saveRpls(inputs: SaveReadyPlanLocationInput[], opts: { membershipChanged?: boolean } = {}) {
    const { data } = await save({ variables: { inputs } });
    const draftId = data?.saveReadyPlanLocations.readyPlanLocations.first?.readyPlan.aggregateDraft?.id;
    if (draftId && draftId !== planPackage.version.id) return navigateToRpav(draftId);
    if (opts.membershipChanged) {
      const { data: fresh } = await refetch();
      const freshDraftId = fresh?.planPackage?.aggregateDraft?.id;
      if (freshDraftId && freshDraftId !== planPackage.version.id) navigateToRpav(freshDraftId);
    }
  }

  const { getFormState } = useFormStates<RoomFormValue, PlanPackageLocationsV2_LocationFragment>({
    config: roomConfig,
    getId: (rpl) => rpl.id,
    map: (rpl) => ({
      id: rpl.id,
      roomTypeId: rpl.roomType?.id,
      marketingName: rpl.marketingName,
      roomNumber: rpl.roomNumber,
    }),
    autoSave: async (os) => {
      const changed = os.changedValue;
      await saveRpls([
        // The backend recalcs the room's Location from roomNumber whenever roomType changes
        { ...changed, ...("roomTypeId" in changed ? { roomNumber: os.roomNumber.value } : {}) },
      ]);
    },
  });

  /**
   * Every proposal on the tab as one form, rather than a nested list on each row's form state:
   * `useFormStates` re-syncs a row from its input on every `getFormState` call, and a nested list
   * turns that into an observable mutation inside the tables' MobX computeds.
   *
   * FIXME: `saveRpls` being its own mutation causes us to need TWO form states. So we run the risk of
   * one request succeeding, and the other failing
   * As we see now:
   * - We get a delete room request (aep relying on underlying afp that )
   * - we accept that update
   * - It fails because that room is tied to an option
   * - we will need to resurface the `aiMode` row and its suggestions (currently broken as of 12:20pm) then we will
   *    just HOPE that the user has the knowledge to see the error appear, alongside the aiMode resurfacing, and then
   *    REJECT the proposal OR unhook the attached option then come back and accept
   * extra musing: this does bring up an issue in order of operations possibly between tabs in a large CD import
   *     IE: The locations, options and takeoffs are so tightly coupled that we may need to guide users in a specific acceptance order
   */
  const proposalsForm = useFormState({
    config: aiProposalsConfig,
    init: { input: roomProposals, map: (rp) => ({ fieldProposals: flattenRoomProposals(rp) }) },
    autoSave: async (state) => {
      const statuses = toResolveInputs(state.changedValue.fieldProposals);
      if (statuses.isEmpty) return;
      // A fully-ignored CREATE, or a fully-accepted DELETE, means the room itself goes away.
      const deletes = [...roomProposals.entries()]
        .filter(([rplId, proposal]) => roomNeedsDelete(proposal, currentStatuses(rplId)))
        .map(([rplId]) => ({ id: rplId, delete: true }));
      try {
        if (deletes.nonEmpty) await saveRpls(deletes, { membershipChanged: true });
        await resolveProposals({ variables: { input: statuses } });
      } catch {
        // The save failed, so nothing was resolved server-side — but we already flipped the statuses
        // locally, which makes the row look reviewed. Put them back to what the server still holds so
        // the wash and the proposals resurface; otherwise the user is left with an error toast and a
        // row that looks done. Reverting to the init value also un-dirties the form, so this does not
        // re-trigger auto-save.
        restoreStoredProposalStatuses(statuses.map((s) => s.id));
        // Apollo already shows the error; rethrowing would escape the auto-save timer.
      }
    },
  });

  /** What's actually stored server-side for each proposal, as of the last load — independent of any optimistic local flip. */
  const storedProposalStatuses = useMemo(
    () =>
      new Map(
        [...roomProposals.values()].flatMap((p) =>
          p.fieldProposals.flatMap((fp) => (fp.id && fp.status ? [[fp.id, fp.status] as const] : [])),
        ),
      ),
    [roomProposals],
  );

  /** Puts a failed batch's statuses back, so its proposals come back up for review. */
  function restoreStoredProposalStatuses(ids: string[]): void {
    ids.forEach((id) => {
      const row = proposalsForm.fieldProposals.rows.find((r) => r.id.value === id);
      const status = storedProposalStatuses.get(id);
      if (row && status) row.status.set(status);
    });
  }

  /** The ids of one room's proposals, for pulling its rows out of the tab-level form. */
  function proposalIds(rplId: string): Set<string> {
    return new Set((roomProposals.get(rplId)?.fieldProposals ?? []).flatMap((fp) => (fp.id ? [fp.id] : [])));
  }

  /** The live status of each of a room's proposals, as the user has left them. */
  function currentStatuses(rplId: string): AiFieldProposalStatus[] {
    const ids = proposalIds(rplId);
    return proposalsForm.fieldProposals.rows
      .filter((row) => !!row.id.value && ids.has(row.id.value))
      .map((row) => row.status.value)
      .compact();
  }

  /** Every proposal for a room, including `active`, which has no editable column behind it. */
  const getRoomProposals: RoomProposalsLookup = (rplId) => {
    const ids = proposalIds(rplId);
    return proposalsForm.fieldProposals.rows.filter((row) => !!row.id.value && ids.has(row.id.value));
  };

  const getFieldProposal: FieldProposalLookup = (rplId, fieldName) => {
    const ids = proposalIds(rplId);
    return proposalsForm.fieldProposals.rows.find(
      (row) => row.fieldName.value === fieldName && !!row.id.value && ids.has(row.id.value),
    );
  };

  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const lastNumberedGroup = groups.filter((group) => isNumberedLevel(group.location.name)).last;

  function isDuplicateFullRoomNumber(rplId: string, fullRoomNumber: number): boolean {
    return groups.some(({ location, rooms }) =>
      rooms.some(
        (room) =>
          room.id !== rplId &&
          formatFullRoomNumber(location.name, getFormState(room).roomNumber.value ?? 0) === fullRoomNumber,
      ),
    );
  }

  // Derived from the server's proposals so the banner's count and each row's banner always agree.
  const roomSummaries: RoomSummary[] = useMemo(
    () =>
      groups.flatMap(({ location, rooms }) =>
        rooms.map((rpl) => ({ rplId: rpl.id, levelName: location.name, fullRoomNumber: rpl.fullRoomNumber })),
      ),
    [groups],
  );
  // Both read the live proposal statuses out of `proposalsForm`, so a warning clears the moment its
  // proposal is resolved and the banner clears on Accept All / Ignore All — no refetch needed.
  // Safe to read here: `proposalsForm` is a single `useFormState`, already materialized, unlike the
  // lazily-created per-row states behind `getFormState`.
  const warnings = useComputed(
    () =>
      deriveRoomWarnings(roomSummaries, (rplId) => {
        const proposal = getFieldProposal(rplId, "roomNumber");
        return proposal && { status: proposal.status.value, fieldValue: proposal.fieldValue.value };
      }),
    [roomSummaries, proposalsForm],
  );
  const hasPending = useComputed(() => hasPendingProposals(proposalsForm.fieldProposals.rows), [proposalsForm]);

  /** Accept All / Ignore All, which `proposalsForm`'s auto-save then persists. */
  function reconcileAll(decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected) {
    groups.forEach(({ location, rooms }) =>
      rooms.forEach((rpl) =>
        reconcilePendingAiProposals(
          roomProposalBindings(getFormState(rpl), rpl.id, location.name, getFieldProposal),
          decision,
          getRoomProposals(rpl.id),
        ),
      ),
    );
  }

  return (
    <div css={Css.mt4.$}>
      {aiEnabled && hasPending && (
        <div css={Css.mb3.$}>
          <PlanPackageAiReviewBanner hasPending={hasPending} warnings={warnings.length} onReconcileAll={reconcileAll} />
        </div>
      )}
      <FormSectionLayout
        title="Locations"
        description="Add the levels and rooms of this plan, and keep room numbers in sync with the construction documents."
        withAutoSave
        actions={[
          {
            label: "Add",
            icon: "plus",
            variant: "secondary",
            disabled,
            onClick: () =>
              openModal({
                content: (
                  <AddLocationModal
                    planPackageId={planPackage.id}
                    groups={groups}
                    levels={levels}
                    roomTypes={roomTypes}
                    saveRpls={saveRpls}
                  />
                ),
              }),
          },
        ]}
        initialFields={
          <div css={Css.df.fdc.gap3.pb8.w100.$}>
            {groups.isEmpty && <div>No locations found</div>}
            {groups.map((group) => (
              <LevelLocationsTable
                key={group.location.id}
                group={group}
                roomTypes={roomTypes}
                getFormState={getFormState}
                disabled={disabled}
                saveRpls={saveRpls}
                isDuplicateFullRoomNumber={isDuplicateFullRoomNumber}
                roomProposals={roomProposals}
                getFieldProposal={getFieldProposal}
                getRoomProposals={getRoomProposals}
                warnings={warnings}
                // Levels 1 and 2 can't be deleted while Level 3 exists, so only offer the last numbered level
                showLevelDelete={
                  !!group.levelRpl && (!isNumberedLevel(group.location.name) || group === lastNumberedGroup)
                }
              />
            ))}
          </div>
        }
      />
    </div>
  );
}

const roomConfig: ObjectConfig<RoomFormValue> = {
  id: { type: "value" },
  roomTypeId: { type: "value" },
  marketingName: { type: "value" },
  roomNumber: { type: "value" },
};
